import { ActionMap } from "interfaces/models/context";
import {
  InspectionContent,
  UpdateInspectionContent,
} from "interfaces/models/inspectionContent";
import { InspectionItem } from "interfaces/models/inspectionItem";
import {
  CheckboxAdditionalInfo,
  InspectionItemResult,
} from "interfaces/models/inspectionItemResult";
import { z, ZodType } from "zod";
import { DataType, types } from "../../setup/define";

export type ElementType<T extends ReadonlyArray<unknown>> =
  T extends ReadonlyArray<infer ElementType> ? ElementType : never;

export const DEFAULT_CHECKBOX_ADDITIONAL_INFO: CheckboxAdditionalInfo = {
  mapValues: {
    "0": { label: "合格チェックボックスのラベル", displayValue: "良" },
    "1": { label: "不合格チェックボックスのラベル", displayValue: "不良" },
  },
};

export enum Types {
  ADD_ITEM = "ADD_ITEM",
  DELETE_ITEM = "DELETE_ITEM",
  UPDATE_ITEM = "UPDATE_ITEM",
  ADD_ITEM_RESULT = "ADD_ITEM_RESULT",
  DELETE_ITEM_RESULT = "DELETE_ITEM_RESULT",
  UPDATE_ITEM_RESULT = "UPDATE_ITEM_RESULT",
  UPDATE_CONTENT = "UPDATE_CONTENT",
  COPY_ITEM = "COPY_ITEM",
  INIT_DATA = "INIT_DATA",
}

export type FormBuilderPayload = {
  [Types.ADD_ITEM]: null;
  [Types.ADD_ITEM_RESULT]: { contentItemId: string };
  [Types.DELETE_ITEM]: { id: string };
  [Types.UPDATE_ITEM]: { id: string } & Partial<InspectionItem>;
  [Types.DELETE_ITEM_RESULT]: {
    id: string;
  };
  [Types.UPDATE_ITEM_RESULT]: { id: string } & Partial<InspectionItemResult> & {
      initData?: Partial<InspectionItemResult>;
    };
  [Types.UPDATE_CONTENT]: Partial<InspectionContent>;
  [Types.COPY_ITEM]: { id: string };
  [Types.INIT_DATA]: FormBuilderState;
};

export type FormBuilderActions =
  ActionMap<FormBuilderPayload>[keyof ActionMap<FormBuilderPayload>];

export type FormBuilderState = {
  inspectionContent: InspectionContent;
  contentItems: InspectionItem[];
  itemResults: InspectionItemResult[];
  defaultState?: Omit<FormBuilderState, "defaultState">;
  mapRowOrderInfo?: Record<string, { index: number; total: number }>;
};

export interface FormBuilderContextType {
  state: FormBuilderState;
  isSavingContent: boolean;
  errors: { [key: string]: string };
  dispatch: React.Dispatch<FormBuilderActions>;
  onSaveContent: () => Promise<UpdateInspectionContent | undefined>;
}

export const isStringNotEmpty = z
  .string()
  .refine((s) => !!s.trim(), "空白にしないでください。");

export const schema = {
  inspectionContent: z.object({
    additionInfo: z.string(),
  }) satisfies ZodType<Partial<InspectionContent>>,
  contentItems: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      order: z.string(),
      inspectionContentId: z.string(),
    }) satisfies ZodType<InspectionItem>
  ),
  itemResult: (inspectionItemResult: InspectionItemResult) => {
    const { validate } = types[inspectionItemResult?.dataType] || {};
    const schema: any = {
      id: z.string(),
      name: isStringNotEmpty,
      dataType: z.lazy(() => z.nativeEnum(DataType)),
      order: z.string(),
      contentItemId: z.string(),
    };

    if (validate) schema.additionInfo = validate?.();

    return z.object(schema);
  },
};

export const validatePartialSchemaContent = (
  path: Partial<z.infer<typeof schema.inspectionContent>>
) => schema.inspectionContent.partial().safeParse(path)?.success;

export const validatePartialSchemaItems = (
  path: Partial<z.infer<typeof schema.contentItems>>
) => schema.contentItems.element.partial().safeParse(path)?.success;
